import { useEffect, useRef, useState } from 'react';

/**
 * Minimal scroll-reveal primitive. Uses {@link IntersectionObserver} to
 * flip {@code isVisible} to {@code true} the first time the target crosses
 * the viewport threshold, then disconnects so the element stays visible on
 * subsequent scrolls (no repeating animations).
 *
 * <p>No animation library required — pair with Tailwind transition classes
 * on the returned {@code ref}'d element.
 *
 * @param threshold fraction of the element that must be visible before
 *                  firing. Default 0.12 — enough that the animation kicks
 *                  in as the section "enters," not only when fully on-screen.
 */
export function useScrollReveal<T extends HTMLElement = HTMLDivElement>(threshold = 0.12) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;

    // Respect prefers-reduced-motion: skip the transition entirely.
    const mql = window.matchMedia('(prefers-reduced-motion: reduce)');
    if (mql.matches) {
      setIsVisible(true);
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, [threshold]);

  return { ref, isVisible };
}
